import { getAuthData } from "../features/auth/session.js";
import { useEffect, useRef, useState } from "react";
import { IconX, IconTrophy, IconCards, IconRefresh } from "./icons.jsx";
import { RatingAvatar } from "./RatingAvatar.jsx";
import { postJson } from "../api/client.jsx";
import "./PublicProfileModal.css";

const number = value => value.toLocaleString("ru-RU");

export function PublicProfileModal({ player, onClose }) {
  const dialog = useRef(null);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [period, setPeriod] = useState("season");
  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      element.close();
      document.body.style.overflow = overflow;
      previousFocus?.focus();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    let active = true;
    setError(null);
    setProfile(null);
    postJson("/api/players/profile", {
      initData: getAuthData() || "",
      userId: player.userId,
    }, controller.signal).then(data => {
      if (active) setProfile(data);
    }).catch(error => {
      if (active) setError(error.message.endsWith(": 404") ? "Игрок не найден." : "Не удалось загрузить профиль. Попробуйте ещё раз.");
    }).finally(() => window.clearTimeout(timeout));
    return () => { active = false; window.clearTimeout(timeout); controller.abort(); };
  }, [player.userId, attempt]);
  const season = period === "season";
  const progress = profile?.collection_total ? Math.min(100, Math.round(profile.collection_owned / profile.collection_total * 100)) : 0;
  return <dialog ref={dialog} className="mc-public-dialog" aria-labelledby="public-profile-title"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="mc-public-profile">
      <header className="mc-public-toolbar">
        <span>КАРТОЧКА ИГРОКА</span>
        <button className="mc-icon-button" type="button" onClick={onClose} aria-label="Закрыть профиль" autoFocus><IconX /></button>
      </header>
      <div className="mc-public-hero">
        <div className="mc-public-portrait"><RatingAvatar name={profile?.name || player.name} url={profile?.avatarUrl || player.avatarUrl} /></div>
        <h2 id="public-profile-title">{profile?.name || player.name}</h2>
        {(profile?.title || player.title) && <p className="mc-public-title">{profile?.title || player.title}</p>}
        {player.place && <span className="mc-public-standing"><IconTrophy /> #{player.place} · {player.ratingLabel}</span>}
      </div>
      {!profile && !error && <div className="mc-public-loading" role="status"><span />Загружаем профиль…</div>}
      {error && <div className="mc-public-error" role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}><IconRefresh /> Повторить</button></div>}
      {profile && <div className="mc-public-content">
        {profile.description && <p className="mc-public-description">{profile.description}</p>}
        <section className="mc-public-collection" aria-label="Коллекция игрока">
          <div><IconCards /><span><small>Текущая вселенная</small><b>{profile.universe}</b></span></div>
          <div className="mc-public-collection-count"><span>Коллекция</span><b>{number(profile.collection_owned)} <small>/ {number(profile.collection_total)}</small></b></div>
          <div className="mc-public-progress" role="progressbar" aria-label="Собрано коллекции" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${progress}%` }} /></div>
        </section>
        <div className="mc-public-period" role="group" aria-label="Период статистики">
          <button type="button" aria-pressed={season} onClick={() => setPeriod("season")}>Этот сезон</button>
          <button type="button" aria-pressed={!season} onClick={() => setPeriod("all")}>За всё время</button>
        </div>
        <div className="mc-public-stats">
          <div><IconTrophy /><strong>{number(season ? profile.season_points : profile.total_points)}</strong><small>PTS · {season ? "за сезон" : "за всё время"}</small></div>
          <div><IconCards /><strong>{number(season ? profile.opened_season : profile.opened_total)}</strong><small>Открыто карт · {season ? "сезон" : "всего"}</small></div>
        </div>
        {profile.joined_at && <p className="mc-public-joined">В MetaCard с {new Date(profile.joined_at).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Moscow" })}</p>}
      </div>}
    </div>
  </dialog>;
}
